import UTIF from 'utif';

export const fileUtils = {
  toBase64: (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  },

  resizeImage: (file: File, maxWidth = 1999, maxHeight = 767): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        // Calculate aspect ratio
        const aspectRatio = img.width / img.height;

        // Initialize new dimensions
        let newWidth = img.width;
        let newHeight = img.height;

        // Scale down if necessary while maintaining aspect ratio
        if (newWidth > maxWidth) {
          newWidth = maxWidth;
          newHeight = newWidth / aspectRatio;
        }

        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          newWidth = newHeight * aspectRatio;
        }

        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Draw resized image
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Convert to base64 with reduced quality
        const base64 = canvas.toDataURL('image/jpeg', 0.8);

        // Cleanup
        URL.revokeObjectURL(img.src);

        resolve(base64);
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
    });
  },

  convertTiffToJpeg: async (tiffFile: File): Promise<File | null> => {
    let arrayBuffer: ArrayBuffer | null = null;
    let canvas: HTMLCanvasElement | null = null;

    try {
      arrayBuffer = await tiffFile.arrayBuffer();
      const ifds = UTIF.decode(arrayBuffer);
      UTIF.decodeImage(arrayBuffer, ifds[0]);
      const rgba = UTIF.toRGBA8(ifds[0]);

      // Use original dimensions - no resizing
      const width = ifds[0].width;
      const height = ifds[0].height;

      // Create canvas with original dimensions
      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // Draw original image to canvas
      const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height);
      ctx.putImageData(imageData, 0, 0);

      // Convert canvas to blob using Promise
      return new Promise((resolve) => {
        canvas!.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File object from the blob
              const jpegFile = new (window as any).File([blob], tiffFile.name.replace(/\.tiff?$/i, '.jpg'), {
                type: 'image/jpeg',
              });
              resolve(jpegFile);
            } else {
              resolve(null);
            }
          },
          'image/jpeg',
          0.9
        );
      });
    } catch (error) {
      console.error('Error converting TIFF to JPEG:', error);
      return null;
    } finally {
      // Clean up any remaining resources
      if (arrayBuffer) {
        arrayBuffer = null;
      }
      if (canvas) {
        canvas.remove();
        canvas = null;
      }
    }
  },

  compressImageForPreview: (file: File): Promise<{ previewUrl: string; originalFile: File }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      img.onload = () => {
        try {
          // Calculate aspect ratio
          const aspectRatio = img.width / img.height;

          // Reasonable dimensions for good quality thumbnails
          let newWidth = img.width;
          let newHeight = img.height;

          // Scale down to max 1024x1024 for good quality
          if (newWidth > 1024) {
            newWidth = 1024;
            newHeight = newWidth / aspectRatio;
          }

          if (newHeight > 1024) {
            newHeight = 1024;
            newWidth = newHeight * aspectRatio;
          }

          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Could not get canvas context');
          }

          // Draw resized image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Convert to base64 with good quality
          const previewUrl = canvas.toDataURL('image/jpeg', 0.8);

          // Cleanup immediately
          URL.revokeObjectURL(objectUrl);
          canvas.remove();
          img.src = '';

          resolve({
            previewUrl,
            originalFile: file,
          });
        } catch (error) {
          // Cleanup on error
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };
    });
  },
};
